// ===== BEGIN FILE: src/components/MobileChatList.tsx =====
/**
 * MobileChatList Component
 * 
 * WhatsApp-style interface showing all life areas as a vertical list.
 * Clicking a life area opens a chat specific to that area.
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  ListItemContent,
  Typography,
  Sheet,
  IconButton,
  Divider
} from '@mui/joy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LIFE_AREAS, type LifeAreaKey } from '../utils/lifeAreas';

interface MobileChatListProps {
  onSelectLifeArea: (lifeArea: LifeAreaKey) => void;
  onClose: () => void;
}

export default function MobileChatList({ onSelectLifeArea, onClose }: MobileChatListProps) {
  const lifeAreaKeys = Object.keys(LIFE_AREAS) as LifeAreaKey[];

  return (
    <Sheet
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        bgcolor: 'background.body',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* ShreemAstro Header — matches AppShell mobile top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          background: 'linear-gradient(135deg, #fff8f8 0%, #fdf0f2 50%, #fff5f6 100%)',
          borderBottom: '1px solid rgba(184,48,80,0.14)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'saturate(180%) blur(10px)',
        }}
      >
        <Box
          component="img"
          src="/Logo.png"
          alt="ShreemAstro"
          onClick={onClose}
          sx={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0, cursor: 'pointer' }}
        />
        <Typography
          onClick={onClose}
          sx={{
            fontFamily: '"Amita", serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            background: 'linear-gradient(135deg, #b83050 0%, #7a1a2e 45%, #4a0a18 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '1px',
            lineHeight: 1,
            flex: 1,
            cursor: 'pointer',
          }}
        >
          ShreemAstro
          <Box component="sup" sx={{ fontSize: '0.6em', fontWeight: 400, letterSpacing: 0, verticalAlign: 'super', lineHeight: 0, WebkitTextFillColor: 'rgba(184,48,80,1)' }}>™</Box>
        </Typography>
        <IconButton
          variant="plain"
          onClick={onClose}
          sx={{ minWidth: 36, minHeight: 36, color: '#7a1a2e', '&:hover': { bgcolor: 'rgba(184,48,80,0.08)' } }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* Life Areas List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>

        {/* ── Ask Shreem hero band ── */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #3a0812 0%, #6b1528 40%, #b83050 80%, #c94560 100%)',
            px: 2.5,
            pt: 2.25,
            pb: 2,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Soft background orbs for depth */}
          <Box sx={{
            position: 'absolute', top: -24, right: -16,
            width: 110, height: 110, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -28, left: 8,
            width: 80, height: 80, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,220,200,0.06) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Logo + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75, position: 'relative' }}>
            <Box
              component="img"
              src="/Logo.png"
              alt=""
              sx={{
                width: 34, height: 34, objectFit: 'contain', borderRadius: '50%',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.18)',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Amita", serif',
                fontSize: '1.55rem',
                fontWeight: 700,
                color: 'rgba(255,255,255,0.96)',
                letterSpacing: '0.3px',
                lineHeight: 1,
              }}
            >
              Ask Shreem
            </Typography>
          </Box>

          {/* Subtitle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: '46px', position: 'relative' }}>
            <Box sx={{
              width: 20, height: 1,
              background: 'rgba(255,210,220,0.5)',
              flexShrink: 0,
            }} />
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 500,
                color: 'rgba(255,210,220,0.88)',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Instant Life Guidance
            </Typography>
            <Box sx={{
              flex: 1, height: 1,
              background: 'linear-gradient(90deg, rgba(255,210,220,0.5) 0%, transparent 100%)',
            }} />
          </Box>
        </Box>

        <List sx={{ p: 0 }}>
          {lifeAreaKeys.map((key, index) => {
            const area = LIFE_AREAS[key];
            return (
              <React.Fragment key={key}>
                <ListItem sx={{ p: 0 }}>
                  <ListItemButton
                    onClick={() => onSelectLifeArea(key)}
                    sx={{
                      py: 2,
                      px: 2,
                      gap: 2,
                      '&:hover': {
                        bgcolor: 'background.level1'
                      }
                    }}
                  >
                    <ListItemDecorator
                      sx={{
                        minWidth: 48,
                        minHeight: 48,
                        bgcolor: 'background.level2',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                      }}
                    >
                      {area.icon}
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="title-md" sx={{ fontWeight: 600 }}>
                        {area.label}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {area.description}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
                {index < lifeAreaKeys.length - 1 && (
                  <Divider sx={{ ml: 10 }} />
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Sheet>
  );
}
